import React from 'react'
import {
    Box,
    Container,
    Flex,
    SimpleGrid,
    Button,
    Card, CardHeader, CardBody,
    HStack, VStack,
    Text,
    Badge,
    Icon,
    Tooltip,
    AlertDialog,
    AlertDialogBody,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogContent,
    AlertDialogOverlay,
    useDisclosure,
    Heading,
    IconButton,
    Drawer,
    DrawerBody,
    DrawerOverlay,
    DrawerContent,
    DrawerCloseButton,
    useBreakpointValue,
    Center,
    Progress
  } from '@chakra-ui/react';
import { AddIcon, CheckCircleIcon, ViewIcon, CloseIcon, TimeIcon, HamburgerIcon, ArrowBackIcon, StarIcon, ArrowRightIcon } from '@chakra-ui/icons';
import { ModernSidebar, UserMenu, BreadcrumbNav, useMobileBottomPadding, MobileBottomNav } from './SharedComponents';

const GoalsView = ({ state, onRouteChange, getOverview, onContinueChapter, onDeleteChapter, onGenerateExercise, onRandomChapter, toggleSidebar, setLoading }) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { onOpen: onSearchOpen } = useDisclosure()
  const { onOpen: onProfileOpen } = useDisclosure()
  const isMobile = useBreakpointValue({ base: true, lg: false })
  const bottomPadding = useMobileBottomPadding()
  const [isGenerating, setIsGenerating] = React.useState(false)

  const handleGenerateExercise = async (goalId) => {
    setIsGenerating(true)
    try {
      await onGenerateExercise(goalId)
    } finally {
      setIsGenerating(false)
    }
  }

  return (
    <Box minH="100vh" bg="gray.50">
      {/* Mobile Navigation */}
      {isMobile && (
        <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
          <DrawerOverlay />
          <DrawerContent bg="white">
            <DrawerCloseButton color="gray.700" />
            <DrawerBody>
              <ModernSidebar
                state={state}
                onRouteChange={onRouteChange}
                getOverview={getOverview}
                onRandomChapter={onRandomChapter}
                onClose={onClose}
                toggleSidebar={undefined}
              />
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      )}
      
      <Flex>
        {/* Desktop Sidebar */}
        {!isMobile && !state.sidebarCollapsed && (
          <Box 
            w="280px" 
            bg="white" 
            borderRight="1px"
            borderColor="gray.200"
            boxShadow="sm" 
            minH="100vh" 
            position="fixed" 
            left={0} 
            top={0}
            zIndex={10}
            transition="all 0.3s ease"
          >
            <ModernSidebar
              state={state}
              onRouteChange={onRouteChange}
              getOverview={getOverview}
              onRandomChapter={onRandomChapter}
              toggleSidebar={toggleSidebar}
            />
          </Box>
        )}
        
        {/* Main Content */}
        <Box flex={1} ml={isMobile ? 0 : state.sidebarCollapsed ? 0 : "280px"} transition="margin-left 0.3s ease" minW={0} overflow="hidden">
          {/* Top Navigation */}
          <Box bg="white" borderBottom="1px" borderColor="gray.200" px={{ base: 4, md: 6 }} py={{ base: 3, md: 3 }}>
            <Flex align="center" justify="space-between">
              <HStack spacing={3}>
                {isMobile && (
                  <IconButton
                    icon={<HamburgerIcon />}
                    onClick={onOpen}
                    variant="ghost"
                    colorScheme="brand"
                    size="sm"
                  />
                )}
                {!isMobile && state.sidebarCollapsed && (
                  <IconButton
                    icon={<HamburgerIcon />}
                    onClick={toggleSidebar}
                    variant="ghost"
                    color="brand.600"
                    size="sm"
                    _hover={{
                      color: 'brand.700',
                      bg: 'brand.50'
                    }}
                  />
                )}
                {!isMobile && (
                  <>
                    <IconButton
                      icon={<ArrowBackIcon />}
                      variant="ghost"
                      colorScheme="brand"
                      onClick={() => onRouteChange('allGoalsView')}
                      aria-label="Back"
                      size="sm"
                    />
                    <Text
                      fontSize="lg"
                      fontWeight="semibold"
                      color="gray.800"
                    >
                      {getCurrentGoalName(state)}
                    </Text>
                  </>
                )}
              </HStack>

              <HStack spacing={4}>
                {!isMobile && (
                  <Button
                    leftIcon={<StarIcon />}
                    variant="outline"
                    colorScheme="purple"
                    size="sm"
                    onClick={() => handleGenerateExercise(state.currentGoalId)}
                    isLoading={isGenerating}
                    loadingText="Creating..."
                  >
                    New Exercises
                  </Button>
                )}
                <UserMenu onRouteChange={onRouteChange} username={state.username} />
              </HStack>
            </Flex>
          </Box>
          
          {/* Breadcrumb Navigation */}
          <BreadcrumbNav 
            route={state.route} 
            goalName={getGoalName(state)}
            onRouteChange={onRouteChange} 
          />
          
          {/* Goal Progress Header */}
          <Box bg="white" borderBottom="1px" borderColor="gray.200" px={{ base: 4, md: 6 }} py={{ base: 2, md: 3 }}>
            <GoalProgressHeader state={state} />
          </Box>

          {/* Practices Grid */}
          <Container maxW="7xl" py={{ base: 4, md: 8 }} px={{ base: 4, md: 6 }} pb={bottomPadding}>
            <Practices
              state={state}
              onContinueChapter={onContinueChapter}
              onDeleteChapter={onDeleteChapter}
              setLoading={setLoading}
              onGenerateExercise={handleGenerateExercise}
              isGenerating={isGenerating}
            />
          </Container>
        </Box>
      </Flex>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav
        currentRoute="practicesView"
        onRouteChange={onRouteChange}
        onRandomChapter={onRandomChapter}
        onSearchOpen={onSearchOpen}
        onProfileOpen={onProfileOpen}
      />
    </Box>
  )
}

const GoalProgressHeader = ({ state }) => {
  if (!state.overview.exists || !state.overview.items) return null

  const exercises = Array.isArray(state.overview.items) ? state.overview.items : [];
  const totalExercises = exercises.length;
  const completedExercises = exercises.filter(ex => ex.completed === true).length;
  const progress = totalExercises > 0 ? Math.floor((completedExercises / totalExercises) * 100) : 0;
  const goalName = getCurrentGoalName(state);

  return (
    <VStack spacing={2} align="stretch">
      <HStack justify="space-between" align="center" gap={2}>
        <VStack align="start" spacing={0} flex={1} minW={0}>
          <Heading size={{ base: 'xs', md: 'sm' }} color="gray.800" noOfLines={1}>{goalName}</Heading>
          <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.600">
            {completedExercises} of {totalExercises} completed
          </Text>
        </VStack>

        <Text fontSize={{ base: 'sm', md: 'md' }} fontWeight="700" color={progress === 100 ? "green.600" : "brand.600"} flexShrink={0}>
          {progress}%
        </Text>
      </HStack>
      <Progress value={progress} size={{ base: 'xs', md: 'sm' }} colorScheme={progress === 100 ? "green" : "brand"} borderRadius="full" />
    </VStack>
  )
}

const Practices = ({ state, onContinueChapter, onDeleteChapter, setLoading, onGenerateExercise, isGenerating }) => {
  const overview = state.overview
  const isMobile = useBreakpointValue({ base: true, lg: false })

  if (!overview.exists || !overview.items || overview.items.length === 0) {
    return (
      <Center py={20}>
        <VStack spacing={6}>
          <Box textAlign="center">
            <Icon as={StarIcon} boxSize={12} color="gray.400" mb={4} />
            <Heading size="lg" color="gray.600" mb={2}>
              No exercises yet
            </Heading>
            <Text color="gray.500" fontSize="lg">
              Generate your first daily practice to begin this journey
            </Text>
          </Box>
          <Button
            leftIcon={<AddIcon />}
            colorScheme="brand"
            size="lg"
            onClick={() => onGenerateExercise(state.currentGoalId)}
            isLoading={isGenerating}
            loadingText="Generating..."
          >
            Generate First Exercise
          </Button>
        </VStack>
      </Center>
    )
  }

  return (
    <VStack spacing={6} align="stretch">
      <Heading size="md" color="gray.700">Your Daily Practices</Heading>

      <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={{ base: 4, md: 6 }}>
        {(Array.isArray(overview.items) ? overview.items : []).map((practice, index) => (
          <PracticeCard
            key={practice.id}
            practice={practice}
            index={index}
            state={state}
            onContinueChapter={onContinueChapter}
            onDeleteChapter={onDeleteChapter}
            setLoading={setLoading}
          />
        ))}
      </SimpleGrid>

      {/* Mobile New Exercise Button */}
      {isMobile && (
        <Box textAlign="center" pt={2}>
          <Button
            leftIcon={<StarIcon />}
            colorScheme="purple"
            size="md"
            onClick={() => onGenerateExercise(state.currentGoalId)}
            isLoading={isGenerating}
            loadingText="Creating..."
            w="full"
          >
            New Exercises
          </Button>
        </Box>
      )}

      <Box textAlign="center" pt={4}>
        <Text fontSize="sm" color="gray.600" fontStyle="italic">
          Complete them in order for the best learning progression
        </Text>
      </Box>
    </VStack>
  )
}

const PracticeCard = ({ practice, index, state, onContinueChapter, onDeleteChapter, setLoading }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = React.useRef();
  const isCompleted = practice.completed || false;
  const isNext = !isCompleted && index === getNextPracticeIndex(Array.isArray(state.overview.items) ? state.overview.items : []);

  const handleDelete = async () => {
    await onDeleteChapter(practice.id);
    onClose();
  };

  const handleStart = async () => {
    await onContinueChapter(practice.id);
  };

  const getCardVariant = () => {
    if (isCompleted) return { bg: 'white', borderColor: 'green.200' }
    if (isNext) return { bg: 'brand.50', borderColor: 'brand.200' }
    return { bg: 'white', borderColor: 'gray.200' }
  }

  const cardVariant = getCardVariant();

  return (
    <Card
      {...cardVariant}
      border="2px"
      transition="all 0.2s ease"
      _hover={{
        transform: { base: 'none', md: 'translateY(-4px)' },
        boxShadow: 'lg',
      }}
      _active={{
        transform: 'scale(0.98)',
      }}
      cursor="pointer"
      position="relative"
      onClick={handleStart}
      overflow="visible"
    >
      {isNext && (
        <Box
          position="absolute"
          top={-2}
          right={-2}
          bg="brand.500"
          color="white"
          borderRadius="full"
          w={{ base: 5, md: 6 }}
          h={{ base: 5, md: 6 }}
          display="flex"
          alignItems="center"
          justifyContent="center"
          fontSize="xs"
          fontWeight="bold"
          zIndex={1}
        >
          !
        </Box>
      )}

      <CardHeader pb={2} px={{ base: 4, md: 5 }} pt={{ base: 3, md: 5 }}>
        <HStack justify="space-between" align="start">
          <VStack align="start" spacing={{ base: 1, md: 2 }} flex={1}>
            <HStack spacing={{ base: 1, md: 2 }} wrap="wrap">
              <Badge
                colorScheme="purple"
                variant="subtle"
                fontSize={{ base: '2xs', md: 'xs' }}
                px={{ base: 1.5, md: 2 }}
                py={0.5}
                borderRadius="md"
              >
                Day {index + 1}
              </Badge>
              <Badge
                colorScheme={isCompleted ? 'green' : 'brand'}
                variant={isCompleted || isNext ? 'solid' : 'subtle'}
                fontSize={{ base: '2xs', md: 'xs' }}
                px={{ base: 1.5, md: 2 }}
                py={0.5}
                borderRadius="md"
              >
                <HStack spacing={1}>
                  <Icon as={isCompleted ? CheckCircleIcon : isNext ? ArrowRightIcon : TimeIcon} boxSize={{ base: 2.5, md: 3 }} />
                  <Text display={{ base: 'none', sm: 'inline' }}>
                    {isCompleted ? 'Done' : isNext ? 'Next' : 'Soon'}
                  </Text>
                </HStack>
              </Badge>
            </HStack>

            <Heading size={{ base: 'xs', md: 'sm' }} noOfLines={2} color="gray.800">
              {practice.name}
            </Heading>
          </VStack>

          <Tooltip label="Delete Practice" placement="top">
            <IconButton
              icon={<CloseIcon />}
              size="xs"
              variant="ghost"
              color="red.400"
              _hover={{
                color: 'red.600',
                bg: 'red.50'
              }}
              onClick={(e) => {
                e.stopPropagation()
                onOpen()
              }}
              zIndex={2}
              ml={1}
            />
          </Tooltip>
        </HStack>
      </CardHeader>

      <CardBody py={{ base: 2, md: 4 }} px={{ base: 4, md: 5 }}>
        <VStack spacing={{ base: 2, md: 4 }} align="stretch">
          {practice.description && (
            <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.600" noOfLines={{ base: 2, md: 3 }}>
              {practice.description}
            </Text>
          )}

          <HStack spacing={2} justify="space-between" wrap="wrap">
            <HStack spacing={1}>
              <TimeIcon boxSize={{ base: 3, md: 4 }} color="gray.500" />
              <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.600">
                {practice.time || '10 mins'}
              </Text>
            </HStack>

            {isNext && (
              <Badge colorScheme="brand" variant="solid" borderRadius="full" px={2} py={0.5} fontSize={{ base: '2xs', md: 'xs' }}>
                Recommended
              </Badge>
            )}
          </HStack>

          <Button
            width="full"
            size={{ base: 'sm', md: 'md' }}
            colorScheme={isCompleted ? 'green' : 'brand'}
            variant={isNext ? 'solid' : 'outline'}
            leftIcon={isCompleted ? <CheckCircleIcon /> : <ViewIcon />}
            onClick={(e) => {
              e.stopPropagation()
              handleStart()
            }}
          >
            {isCompleted ? 'Review' : 'Start'}
          </Button>
        </VStack>
      </CardBody>

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent mx={4}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Practice
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete <strong>{practice.name}</strong>? This action cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose} size={{ base: 'sm', md: 'md' }}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDelete}
                ml={3}
                size={{ base: 'sm', md: 'md' }}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Card>
  )
}

// ModernSidebar component moved to SharedComponents.js

// UserMenu component moved to SharedComponents.js

// Helper functions
const getCurrentGoal = (state) => {
  if (!state.overview.exists || !state.overview.items || !state.currentGoalId) return null
  
  // In the context of GoalsView, we're actually viewing exercises/practices
  // The "overview" contains the exercises for the current goal
  return state.overview.items[0] // Simplified for now
}

const getCurrentGoalName = (state) => {
  // If we have direct goal name from the overview, use it
  if (state.overview.goalName) {
    return state.overview.goalName;
  }
  // Fallback to the old method
  const goal = getCurrentGoal(state)
  return goal ? goal.name : 'Therapeutic Practices'
}

const getNextPracticeIndex = (practices) => {
  if (!practices) return 0
  for (let i = 0; i < practices.length; i++) {
    if (practices[i].completed !== true) return i
  }
  return practices.length - 1
}

const getGoalName = (state) => {
  return getCurrentGoalName(state)
}

export default GoalsView